import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Download, BarChart3, Loader2, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const useOrgScopedHeaders = (token) => {
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  useEffect(() => {
    if (!token || orgId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => { if (!cancelled && ctx?.organizations?.[0]?.id) { const id = ctx.organizations[0].id; setOrgId(id); try { localStorage.setItem('current_organization_id', id); } catch {} } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(orgId ? { 'X-Organization-Id': orgId } : {}) }), [token, orgId]);
  return { headers, orgId };
};

const SECTIONS = [
  { key: 'menu_and_costing', title: 'Menu & Costing', metrics: [['recipes', 'Recipes'], ['ingredients', 'Ingredients'], ['menu_items', 'Menu items']] },
  { key: 'procurement', title: 'Procurement', metrics: [['total_pos', 'Total POs'], ['open_po_value', 'Open PO value (£)']] },
  { key: 'delivery', title: 'Delivery', metrics: [['orders', 'Orders'], ['active', 'Active'], ['paid_revenue', 'Paid revenue (£)']] },
  { key: 'crm', title: 'Guests (CRM)', metrics: [['customers', 'Customers'], ['marketing_opted_in', 'Opted-in'], ['lifetime_spend', 'Lifetime spend (£)']] },
  { key: 'hrm', title: 'People (HRM)', metrics: [['active_headcount', 'Headcount'], ['scheduled_labour_cost', 'Labour cost (£)']] },
  { key: 'documents', title: 'Document Vault', metrics: [['stored', 'Documents stored']] },
];

export const coverageLabel = (status) => ({
  DATA_PRESENT: 'Data present',
  EMPTY: 'Connected, no records',
  NOT_CONFIGURED: 'Source not configured',
})[status] || 'Coverage not assessed';

const DataRoom = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers, orgId } = useOrgScopedHeaders(token);
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!headers['X-Organization-Id']) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bi/overview`, { headers });
      setSnap(res.ok ? await res.json() : null);
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const res = await fetch(`${API_URL}/api/bi/export`, { headers });
    if (!res.ok) return;
    const text = await res.text();
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'data-room-kpis.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const exportPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await fetch(`${API_URL}/api/bi/board-pack`, { headers });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'board-pack.pdf'; a.click();
      URL.revokeObjectURL(url);
    } finally { setDownloadingPdf(false); }
  };

  if (!orgId && !loading) return <div className="max-w-6xl mx-auto p-8 text-ink-muted" data-testid="dataroom-no-org">No active organization. Please log in.</div>;

  return (
    <div className="min-h-screen bg-surface-muted py-8" data-testid="dataroom-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-overline text-accent-500 mb-1">Business Intelligence</div>
            <h1 className="font-display text-3xl text-ink">Data Room</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={exportPdf} disabled={downloadingPdf} data-testid="dataroom-boardpack-btn" className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50 motion-organic">
              <FileText className="w-4 h-4" /> {downloadingPdf ? 'Generating…' : 'Board Pack (PDF)'}
            </button>
            <button onClick={exportCsv} data-testid="dataroom-export-btn" className="flex items-center gap-2 bg-ink text-white px-4 py-2.5 rounded-md text-sm font-medium hover:opacity-90 motion-organic">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>
        : !snap ? <div className="p-10 text-center text-ink-muted" data-testid="dataroom-empty">No data available.</div>
        : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {SECTIONS.map(sec => (
                <div key={sec.key} className="bg-white rounded-xl p-5 shadow-sm border border-surface-border" data-testid={`dataroom-section-${sec.key}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-primary-500" />
                    <h2 className="font-display text-lg text-ink">{sec.title}</h2>
                  </div>
                  <div className="space-y-2">
                    {sec.metrics.map(([k, label]) => (
                      <div key={k} className="flex justify-between text-sm">
                        <span className="text-ink-muted">{label}</span>
                        <span className="tabular-nums font-medium text-ink">{(snap[sec.key] || {})[k] ?? 0}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 border-t border-surface-border pt-3 text-xs" data-testid={`dataroom-coverage-${sec.key}`}>
                    <div className="font-medium text-ink">{coverageLabel(snap.source_coverage?.sections?.[sec.key]?.status)}</div>
                    <div className="mt-1 text-ink-muted">
                      {Object.values(snap.source_coverage?.sections?.[sec.key]?.row_counts || {}).reduce((sum, count) => sum + Number(count || 0), 0)} records across configured collections
                    </div>
                    {(snap.source_coverage?.sections?.[sec.key]?.missing_collections || []).length > 0 && (
                      <div className="mt-1 text-ink-muted">Missing: {snap.source_coverage.sections[sec.key].missing_collections.join(', ')}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-ink-muted" data-testid="dataroom-coverage-limit">
              Source status reflects organization-scoped collection presence and row counts only. It does not assess data completeness, physical work, or integration health.
            </p>
            <div className="text-xs text-ink-muted mt-4" data-testid="dataroom-generated">Snapshot generated {snap.generated_at ? new Date(snap.generated_at).toLocaleString() : ''}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default DataRoom;
